import { useState, useEffect } from "react";
import StateHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import AssamMapFull from "../components/HeatMap/AssamMapFull";
import FacilityListModal from "../components/FacilityListModal";
import { fetchHmisStockStats, fetchDistrictAvailability } from "../data/api";

const SummaryCard3 = ({ title, value, subtitle, color = "blue", onClick }) => (
  <div
    className={`summary-card ${color}`}
    onClick={onClick}
    style={onClick ? { cursor: 'pointer' } : undefined}
    title={onClick ? 'Click to view details' : undefined}
  >
    <h4 className="sc-title">{title}</h4>
    <span className="bg-circle" style={{ backgroundColor: color }}></span>
    <h2 className="sc-value">{value}</h2>
    <p className="sc-subtitle">{subtitle}</p>
  </div>
);

const HeatMapDashboard = () => {
  const [stockStats, setStockStats] = useState(null);
  const [ddwhStats, setDdwhStats] = useState(null);
  const [mcStats, setMcStats] = useState(null);
  const [dhStats, setDhStats] = useState(null);
  const [sdhStats, setSdhStats] = useState(null);
  const [chcStats, setChcStats] = useState(null);
  const [phcStats, setPhcStats] = useState(null);
  const [activeFacType, setActiveFacType] = useState(null);

  useEffect(() => {
    fetchHmisStockStats().then(r => setStockStats(r.data)).catch(console.error);

    // Fetch DDWH, MC, DH availability
    fetchDistrictAvailability('ddwh').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setDdwhStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);

    fetchDistrictAvailability('mc').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setMcStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);

    fetchDistrictAvailability('dh').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setDhStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);

    fetchDistrictAvailability('sdh').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setSdhStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);

    fetchDistrictAvailability('chc').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setChcStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);

    fetchDistrictAvailability('phc').then(r => {
      const rows = r.data;
      const totalItems = rows.reduce((s, d) => s + (parseInt(d.total_stores) || 0), 0);
      const inStock = rows.reduce((s, d) => s + (parseInt(d.stores_above_90) || 0), 0);
      const avg = rows.length > 0 ? (rows.reduce((s, d) => s + parseFloat(d.avail), 0) / rows.length).toFixed(1) : 0;
      setPhcStats({ count: rows.length, avg, totalItems, inStock });
    }).catch(console.error);
  }, []);

  const summaryData = stockStats ? [
    {
      title: "Overall Stock",
      value: `${stockStats.overall_availability}%`,
      color: "blue",
      subtitle: `${stockStats.total_stores} monitored stores`,
      facType: 'all',
    },
    {
      title: "Facilities > 90%",
      value: `${stockStats.above_90} (${((stockStats.above_90 / stockStats.total_stores) * 100).toFixed(1)}%)`,
      color: "green",
      subtitle: `of ${stockStats.total_stores} stores`,
      facType: 'all',
    },
    {
      title: "DDWH Stock",
      value: ddwhStats ? `${ddwhStats.avg}%` : '...',
      color: "orange",
      subtitle: ddwhStats ? `${ddwhStats.count} warehouses | ${ddwhStats.totalItems} items` : 'Loading...',
      facType: 'ddwh',
    },
    {
      title: "Medical College Stock",
      value: mcStats ? `${mcStats.avg}%` : '...',
      color: "red",
      subtitle: mcStats ? `${mcStats.count} MCs | ${mcStats.totalItems} items` : 'Loading...',
      facType: 'mc',
    },
    {
      title: "District Hospital Stock",
      value: dhStats ? `${dhStats.avg}%` : '...',
      color: "purple",
      subtitle: dhStats ? `${dhStats.count} DHs | ${dhStats.totalItems} items` : 'Loading...',
      facType: 'dh',
    },
    {
      title: "SDH Stock",
      value: sdhStats ? `${sdhStats.avg}%` : '...',
      color: "teal",
      subtitle: sdhStats ? `${sdhStats.count} SDHs | ${sdhStats.totalItems} items` : 'Loading...',
      facType: 'sdh',
    },
    {
      title: "CHC/MH Stock",
      value: chcStats ? `${chcStats.avg}%` : '...',
      color: "cyan",
      subtitle: chcStats ? `${chcStats.count} CHC/MH | ${chcStats.totalItems} items` : 'Loading...',
      facType: 'chc',
    },
    {
      title: "PHC Stock",
      value: phcStats ? `${phcStats.avg}%` : '...',
      color: "indigo",
      subtitle: phcStats ? `${phcStats.count} PHCs | ${phcStats.totalItems} items` : 'Loading...',
      facType: 'phc',
    },
  ] : [];

  return (
    <>
      <StateHeader />
      <div className="main" style={{ paddingBottom: 0 }}>
        <div className="summary-left" style={{ marginBottom: 0 }}>
          {summaryData.map((item, index) => (
            <SummaryCard3
              key={index}
              {...item}
              onClick={() => setActiveFacType(item.facType)}
            />
          ))}
        </div>
      </div>
      <div className="main" style={{ paddingTop: 0 }}>
        <AssamMapFull />
      </div>
      {activeFacType && (
        <FacilityListModal
          facType={activeFacType}
          onClose={() => setActiveFacType(null)}
        />
      )}
      <Footer />
    </>
  );
};

export default HeatMapDashboard;
